import { useMemo } from 'react';
import opConfig from 'openpathConfig';
import { useOpQuery } from './useOpQuery';
import { useGetActiveScopes } from './useGetActiveScopes';

export const useFeatureFlag = <T>(
  flag: keyof typeof opConfig,
  orgId?: number,
) => {
  const {
    data: heliumOrgFlags,
    isLoading: isLoadingHeliumOrgFlags,
    isPending: isPendingHeliumOrgFlags,
    isFetching: isFetchingHeliumOrgFlags,
  } = useOpQuery({
    apiEndpointName: 'listOrgFeatureFlags',
    parameters: [orgId!],
    select: (data) => data.json.data,
  });

  /**
   * NOTE: There is logic in the listOrgFeatureFlags handler where if the value is the number 0, then it will get the global flag value. However useOpQuery will not run the query if the value is the number 0 as it is falsy. Therefore, will call the listFeatureFlags endpoint if the orgId is not provided or it is the number 0.
   */
  const { activeScopes, isLoading: isLoadingActiveScopes } = useGetActiveScopes(
    orgId!,
  );

  // This will add an extra guard so that this isn't called without the scopes it can be called with, but we need to fix the source which would be to allow this to be called from APA which means we need to add scopes to the listFeatureFlags endpoint
  const isInternal =
    activeScopes.includes('o:r') ||
    activeScopes.includes('o:w') ||
    activeScopes.includes('o-support:r') ||
    activeScopes.includes('o-support:w');

  const { data: featureFlags } = useOpQuery({
    apiEndpointName: 'listFeatureFlags',
    select: (data) => data.json.data,
    enabled: Boolean(!isLoadingActiveScopes && isInternal && !orgId),
  });

  // Check for the flag value based on helium and environment config
  const flagValue: T | undefined | null = useMemo(() => {
    // Only run after the helium calls finish loading
    if (isLoadingHeliumOrgFlags) {
      return;
    }

    // Check if the flag is set in helium - will get org setting, if set and global default if not
    let heliumFlagValue = null;
    let heliumOrgFlag = null;
    let heliumGlobalFlag = null;

    if (heliumOrgFlags) {
      heliumOrgFlag = (heliumOrgFlags || [])?.find(
        (hof) => hof.featureFlag?.name === flag,
      );
      heliumFlagValue = heliumOrgFlag?.value;
    } else {
      heliumGlobalFlag = (featureFlags || [])?.find((hf) => hf.name === flag);
      heliumFlagValue =
        heliumGlobalFlag?.dataType === 'boolean'
          ? heliumGlobalFlag?.globalValue === '1'
          : heliumGlobalFlag?.globalValue;
    }

    return (
      // If the helium flag exists and the value is set, use. If not set or does not exist, move on.
      heliumFlagValue ??
      // Handle if the flag does not exist or the value was never set in helium
      opConfig[flag]
    );
  }, [featureFlags, flag, heliumOrgFlags, isLoadingHeliumOrgFlags]);

  return {
    value: flagValue,
    isLoading: isLoadingHeliumOrgFlags,
    isFetching: isFetchingHeliumOrgFlags,
    isPending: isPendingHeliumOrgFlags,
  };
};
